import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";
import { getConsent, saveConsent, applyConsent } from "@/lib/consent";
import { Switch } from "@/components/ui/switch";

const CATS = [
  { id: "necessary", name: "Strettamente necessari", desc: "Login, carrello, sicurezza e memorizzazione di queste scelte. Senza di essi il sito non funziona.", locked: true },
  { id: "statistics", name: "Statistiche", desc: "Misurazione anonima e aggregata dell'uso del sito per migliorarne il funzionamento." },
  { id: "marketing", name: "Marketing", desc: "Annunci personalizzati e misurazione delle campagne. Al momento non utilizziamo strumenti di questo tipo." },
];

export function CookieBanner() {
  const [consent, setConsent] = useState(getConsent);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    applyConsent(consent);
    const show = () => { const c = getConsent(); setPrefs({ statistics: !!c?.statistics, marketing: !!c?.marketing }); setOpen(true); setConsent(null); };
    window.addEventListener("hs-open-cookie-prefs", show);
    return () => window.removeEventListener("hs-open-cookie-prefs", show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const done = (p) => { setConsent(saveConsent(p)); setOpen(false); };
  if (consent) return null;

  const btn = "press h-10 px-4 rounded-md text-sm font-semibold";
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5" data-testid="cookie-banner" role="dialog" aria-label="Preferenze cookie">
      <div className="max-w-3xl mx-auto rounded-xl bg-white border-2 border-brand-navy shadow-[6px_6px_0_#EDD93F] p-5 relative max-h-[85vh] overflow-y-auto">
        <button data-testid="cookie-close-btn" aria-label="Chiudi e continua senza accettare" onClick={() => done({})} className="absolute top-3 right-3 text-slate-400 hover:text-brand-navy"><X className="w-5 h-5" /></button>
        <div className="flex items-start gap-3 pr-6">
          <Cookie className="w-6 h-6 text-brand-navy shrink-0 mt-0.5" />
          <div className="space-y-2 text-sm text-slate-700">
            <p className="font-display text-xl font-bold uppercase text-brand-navy">La tua privacy</p>
            <p>
              Usiamo cookie e strumenti tecnici necessari al funzionamento del sito e, solo con il tuo consenso, strumenti statistici.
              Puoi accettare, rifiutare o scegliere per categoria; chiudendo il banner con la X continui con i soli cookie necessari.
              Puoi cambiare idea in qualsiasi momento da "Preferenze cookie" in fondo alla pagina.{" "}
              <Link to="/cookie-policy" className="underline font-semibold text-brand-navy" data-testid="cookie-policy-link">Cookie Policy</Link> ·{" "}
              <Link to="/privacy" className="underline font-semibold text-brand-navy">Privacy Policy</Link>
            </p>
          </div>
        </div>
        {open && (
          <div className="mt-4 space-y-2" data-testid="cookie-prefs-panel">
            {CATS.map((c) => (
              <div key={c.id} className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="text-sm font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.desc}</div>
                </div>
                <Switch data-testid={`cookie-toggle-${c.id}`} checked={c.locked ? true : prefs[c.id]} disabled={c.locked}
                  onCheckedChange={(v) => setPrefs({ ...prefs, [c.id]: v })} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          {open ? (
            <button data-testid="cookie-save-btn" onClick={() => done(prefs)} className={`${btn} border-2 border-brand-navy text-brand-navy`}>Salva le mie scelte</button>
          ) : (
            <button data-testid="cookie-customize-btn" onClick={() => setOpen(true)} className={`${btn} border border-slate-300 text-slate-700 hover:border-brand-navy`}>Personalizza</button>
          )}
          <button data-testid="cookie-reject-btn" onClick={() => done({})} className={`${btn} bg-brand-navy text-white`}>Rifiuta</button>
          <button data-testid="cookie-accept-btn" onClick={() => done({ statistics: true, marketing: true })} className={`${btn} bg-brand-navy text-white`}>Accetta tutti</button>
        </div>
      </div>
    </div>
  );
}
